import { loadFfmpeg, execFfmpeg, disposeFfmpeg } from './ffmpeg-client'

export type VideoFormat = 'mp4' | 'webm'
export type AudioFormat = 'mp3' | 'wav' | 'aac'

/** Lifecycle stage of a conversion for UI feedback. */
export type MediaStage = 'loading' | 'converting'

export interface ConvertOptions {
  input: File
  onProgress?: (progress: number) => void
  /** Engine download/init vs actual transcoding. */
  onStage?: (stage: MediaStage) => void
}

const MAX_INPUT_MB = 400
const MAX_INPUT_BYTES = MAX_INPUT_MB * 1024 * 1024

const VIDEO_INPUT_EXTENSIONS = ['mp4', 'mov', 'mkv', 'webm'] as const
const AUDIO_INPUT_EXTENSIONS = ['mp3', 'wav', 'aac', 'm4a'] as const

const VIDEO_ARGS: Record<VideoFormat, readonly string[]> = {
  mp4: [
    '-c:v', 'libx264', '-preset', 'veryfast', '-crf', '28',
    '-c:a', 'aac', '-b:a', '128k',
    '-movflags', '+faststart',
  ],
  webm: [
    '-c:v', 'libvpx-vp9', '-crf', '35', '-b:v', '0',
    '-c:a', 'libopus', '-b:a', '128k',
  ],
}

const AUDIO_ARGS: Record<AudioFormat, readonly string[]> = {
  mp3: ['-c:a', 'libmp3lame', '-q:a', '2'],
  wav: ['-c:a', 'pcm_s16le'],
  aac: ['-c:a', 'aac', '-b:a', '192k'],
}

const MIME_BY_OUTPUT: Record<string, string> = {
  mp4: 'video/mp4',
  webm: 'video/webm',
  mp3: 'audio/mpeg',
  wav: 'audio/wav',
  aac: 'audio/aac',
}

function extensionOf(file: File): string {
  return file.name.split('.').pop()?.toLowerCase() ?? ''
}

/** Rename the input to a fixed safe name inside the WASM filesystem. */
function safeInputName(file: File): string {
  const ext = extensionOf(file)
  return `input.${ext}`
}

function validateInput(
  file: File,
  allowed: readonly string[],
  kind: 'video' | 'audio',
): void {
  const ext = extensionOf(file)
  if (!allowed.includes(ext as (typeof allowed)[number])) {
    throw new Error(
      `Unsupported ${kind} type ".${ext}". Supported: ${allowed.join(', ')}`,
    )
  }
  if (file.size > MAX_INPUT_BYTES) {
    throw new Error(
      `File too large. Maximum size is ${MAX_INPUT_MB} MB to protect your browser's memory.`,
    )
  }
}

async function convertMedia(
  file: File,
  outputName: string,
  codecArgs: readonly string[],
  options: ConvertOptions,
): Promise<Blob> {
  const { onProgress, onStage } = options

  // Lazy bootstrap: the WASM core (~32MB) is only fetched here, on first use.
  onStage?.('loading')
  await loadFfmpeg()

  onStage?.('converting')
  const inputName = safeInputName(file)
  const data = await file.arrayBuffer()

  const output = await execFfmpeg({
    args: ['-i', inputName, ...codecArgs, '-y', outputName],
    input: { name: inputName, data },
    outputName,
    onProgress,
  })

  if (!output) {
    throw new Error('The converter produced no output. The file may be unsupported.')
  }

  const ext = outputName.split('.').pop() ?? ''
  return new Blob([output], { type: MIME_BY_OUTPUT[ext] ?? 'application/octet-stream' })
}

/** Convert a video file (MP4/MOV/MKV/WEBM) to MP4 or WEBM, fully in-browser. */
export async function convertVideo(
  options: ConvertOptions,
  format: VideoFormat,
): Promise<Blob> {
  validateInput(options.input, VIDEO_INPUT_EXTENSIONS, 'video')
  return convertMedia(options.input, `output.${format}`, VIDEO_ARGS[format], options)
}

/** Convert an audio file (MP3/WAV/AAC/M4A) to MP3, WAV or AAC, fully in-browser. */
export async function convertAudio(
  options: ConvertOptions,
  format: AudioFormat,
): Promise<Blob> {
  validateInput(options.input, AUDIO_INPUT_EXTENSIONS, 'audio')
  return convertMedia(options.input, `output.${format}`, AUDIO_ARGS[format], options)
}

export { disposeFfmpeg }
